// src/pages/settings/ProfileSettingsPage.tsx (or wherever yours lives)
import { Alert, Button, Field, Flex, SimpleGrid, Spinner } from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import { MdLockOpen, MdOutlineMarkEmailRead } from 'react-icons/md'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { getProfileSettings } from '@/api/settings.api'
import type { ProfileSettingsData } from '@/common/profileSettings.schema'
import { normalizeProfileSettings } from '@/common/profileSettings.schema'
import { ChangeEmailDialog } from '@/components/modals/ChangeEmailDialog'
import { ChangePasswordDialog } from '@/components/modals/ChangePasswordDialog'
import { SettingsInput } from './SettingsControls'
import { FormLayout } from '@/components/layout/FormLayout'

export function ProfileSettingsPage() {
  const [data, setData] = useState<ProfileSettingsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [isChangeEmailOpen, setIsChangeEmailOpen] = useState(false)
  const [isPasswordOpen, setIsPasswordOpen] = useState(false)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        const json = await getProfileSettings()
        const normalized = normalizeProfileSettings(json)
        if (!mounted) return
        setData(normalized)
      } catch {
        if (!mounted) return
        setError('Fehler beim Laden.')
        toast.error('Fehler beim Laden')
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  if (loading) {
    return (
      <Flex w="100%" h="300px" align="center" justify="center">
        <Spinner />
      </Flex>
    )
  }

  if (error) {
    return (
      <Flex w="100%" h="300px" align="center" justify="center">
        <Alert.Root status="error">
          <Alert.Indicator />
          <Alert.Description>{error}</Alert.Description>
        </Alert.Root>
      </Flex>
    )
  }

  return (
    <>
      <FormLayout.Root data-testid="profile-settings">
        <FormLayout.Section>
          <FormLayout.Title>Nutzerdaten verwalten</FormLayout.Title>
          <FormLayout.Description>
            Hier können Sie Ihre Anmeldedaten verwalten.
          </FormLayout.Description>
        </FormLayout.Section>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
          {/* EMAIL */}
          <Field.Root>
            <Field.Label>E-Mail-Adresse</Field.Label>
            <SettingsInput value={data?.email || ''} disabled />
            <Button variant="subtle" onClick={() => setIsChangeEmailOpen(true)}>
              <MdOutlineMarkEmailRead />
              E-Mail Adresse aktualisieren
            </Button>
          </Field.Root>

          {/* PASSWORD */}
          <Field.Root>
            <Field.Label>Kennwort</Field.Label>
            <SettingsInput value="••••••••••••" disabled />
            <Button variant="subtle" onClick={() => setIsPasswordOpen(true)}>
              <MdLockOpen />
              Kennwort aktualisieren
            </Button>
          </Field.Root>
        </SimpleGrid>
      </FormLayout.Root>

      <ChangeEmailDialog
        isOpen={isChangeEmailOpen}
        onOpenChange={setIsChangeEmailOpen}
        currentEmail={data?.email || ''}
        onSuccess={(newEmail: string) => {
          setData(prev => (prev ? { ...prev, email: newEmail } : prev))
        }}
      />

      <ChangePasswordDialog
        isOpen={isPasswordOpen}
        onOpenChange={setIsPasswordOpen}
        onSuccess={() => toast.success('Kennwort erfolgreich geändert')}
      />
    </>
  )
}
